import Input, { InputType } from 'components/shared/inputs/input'
import useTranslation from 'utils/hooks/useTranslation'
import { convertToSelect } from 'utils'
import * as dd from 'constants/dropdown'
import { useAppSelector } from 'store/hooks'
import { useSalesNetwork } from 'utils/hooks/useSalesNetworks'
import { useCallback, useEffect } from 'react'
import type { FieldValues } from 'react-hook-form'

type HeatModeDeperditionsConnuesProps = {
  control: any
  errors: any
  deperditions: any
  calculateDeperd: () => void
  watch: any
  getFieldState: any
  setValue: any,
  onSelect?: (name: keyof FieldValues, code?: string, label?: string) => void
}

const HeatModeDeperditionsInvoice: React.FC<HeatModeDeperditionsConnuesProps> = (props) => {
  const [temp, annualBilling, actualSystem, nrgPrice, withECS, nbPeoples, systemAge, dayTemp, nightTemp] = props.watch([
    'heatMode.deperditions.temperature',
    'heatMode.deperditions.annualBilling',
    'heatMode.deperditions.actualSystem',
    'heatMode.deperditions.nrgPrice',
    'heatMode.deperditions.withECS',
    'heatMode.deperditions.nbPeoples',
    'heatMode.deperditions.systemAge',
    'heatMode.deperditions.dayTemp',
    'heatMode.deperditions.nightTemp',
  ]) //NOSONAR

  useEffect(() => {
    if (withECS === '0' && props.getFieldState('heatMode.deperditions.withECS').isDirty)
      props.setValue('heatMode.deperditions.nbPeoples', '0')

    // Set nrgUnit based on actualSystem
    switch (actualSystem) {
      case '1': props.setValue('heatMode.deperditions.nrgUnit', 3); break;
      case '2': props.setValue('heatMode.deperditions.nrgUnit', 1); break;
      case '3': props.setValue('heatMode.deperditions.nrgUnit', 2); break;
    }

    props.calculateDeperd() //NOSONAR
  }, [temp, annualBilling, actualSystem, nrgPrice, withECS, nbPeoples, systemAge, dayTemp, nightTemp]) //NOSONAR

  const data = {
    actualSystem: convertToSelect(dd.actualSystem),
    withECS: convertToSelect(dd.withECS),
    systemAge: convertToSelect(dd.systemAge),
    temperatures: convertToSelect(dd.temperature),
    nbPeoples: convertToSelect(dd.invoiceNbOccupants),
  }

  const { network } = useSalesNetwork()
  const { t } = useTranslation()
  const formStore = useAppSelector((state) => state.heatMode)

  const getEnergyUnit = () => {
    const { deperditions } = props //NOSONAR
    switch (deperditions.actualSystem) {
      case '1': return network?._parameters.currency + '/l'
      case '2': return network?._parameters.currency + '/kWh'
      case '3': return network?._parameters.currency + '/kg'
      default: return '';
    }
  }

  const onFieldChange = useCallback((name: keyof FieldValues, code: any) => {
    const dropdownMap: Record<string, typeof dd.actualSystem> = {
      'heatMode.deperditions.actualSystem': dd.actualSystem,
      'heatMode.deperditions.withECS': dd.withECS,
      'heatMode.deperditions.systemAge': dd.systemAge,
    };
    const dropdown = dropdownMap[name as string];
    const label = dropdown?.find((item) => item.code === code)?.label;
    props.onSelect?.(name, code, label)
  }, [])

  return (
    <>
      <group data-gap="10" data-space="10" data-space-left="0" data-radius="10" data-direction="column">
        <group data-width="auto" data-direction="column" data-justify="end">
          <group data-gap="20" data-align="center" data-name="custom_option">


            <group data-width="auto" data-direction="column" data-justify="end">
              <group data-gap="10" data-space="10" data-space-left='0' data-radius="10" data-direction="column">
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Number}
                      control={props.control}
                      name="heatMode.deperditions.annualBilling"
                      label={`${t('ANNUAL_BILLING')}`}
                      translateLabel={false}
                      size="large"
                      //placeholder={`${t('ANNUAL_BILLING')}`}
                      min={0}
                      step={0.01}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Select}
                      control={props.control}
                      name="heatMode.deperditions.actualSystem"
                      label={`${t('ACTUAL_SYSTEM')}`}
                      translateLabel={false}
                      options={data.actualSystem}
                      allowSearch={true}
                      size="large"
                      //placeholder={`${t('ACTUAL_SYSTEM')}`}
                      translateValue={true}
                      onChange={(e: any) => onFieldChange("heatMode.deperditions.actualSystem", e)}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Number}
                      control={props.control}
                      name="heatMode.deperditions.nrgPrice"
                      label={`${t('ENERGY_PRICE')} ${getEnergyUnit()}`}
                      translateLabel={false}
                      size="large"
                      //placeholder={`${t('ENERGY_PRICE')} ${getEnergyUnit()}`}
                      min={0}
                      step={0.01}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Select}
                      control={props.control}
                      name="heatMode.deperditions.withECS"
                      label={`${t('WITH_ECS')}`}
                      translateLabel={false}
                      options={data.withECS}
                      allowSearch={true}
                      size="large"
                      //placeholder={`${t('WITH_ECS')}`}
                      translateValue={true}
                      onChange={(e: any) => onFieldChange("heatMode.deperditions.withECS", e)}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Select}
                      control={props.control}
                      name="heatMode.deperditions.nbPeoples"
                      label={`${t('OCCUPANTS_NUMBER')}`}
                      translateLabel={false}
                      options={data.nbPeoples}
                      disabled={withECS === '0'}
                      allowSearch={true}
                      size="large"
                    //placeholder={`${t('OCCUPANTS_NUMBER')}`}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Select}
                      control={props.control}
                      label={`${t('SYSTEM_AGE')}`}
                      name="heatMode.deperditions.systemAge"
                      translateLabel={false}
                      options={data.systemAge}
                      allowSearch={true}
                      size="large"
                      onChange={(e: any) => onFieldChange("heatMode.deperditions.systemAge", e)}
                      //placeholder={`${t('SYSTEM_AGE')}`}
                      translateValue={true}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Number}
                      control={props.control}
                      name="heatMode.deperditions.dayTemp"
                      label={`${t('DAY_TEMPERATURE')} (°C)`}
                      translateLabel={false}
                      size="large"
                      //placeholder={`${t('DAY_TEMPERATURE')} (°C)`}
                      min={0}
                      step={1}
                    />
                  </group>
                </group>
                <group data-gap="20">
                  <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
                    <Input
                      type={InputType.Number}
                      control={props.control}
                      name="heatMode.deperditions.nightTemp"
                      label={`${t('NIGHT_TEMPERATURE')} (°C)`}
                      translateLabel={false}
                      size="large"
                      //placeholder={`${t('NIGHT_TEMPERATURE')} (°C)`}
                      min={0}
                      step={1}
                    />
                  </group>
                </group>
              </group>
            </group>


          </group>
        </group>
      </group>
    </>
  )
}

export default HeatModeDeperditionsInvoice
